import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DndContext,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import RotatingLoader from '../../common/ui/RotatingLoader';
import { AccountData, EnrichedEventData, NoteData, OrderData, OrderService, UnavailabilityData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useLanguage } from '../../common/GeneralUtils';
import { useAssignmentConflictHandler } from '../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { localizedLabel } from '../../utils/maskUtils';
import { priceFormatterFromCurrency } from '../../utils/utils';
import { getOrderPage } from '../../utils/AccountUtils';
import { eventUsedTickets } from '../../common/multipleTicketsUtils';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useOrders } from '../swr/useOrders';
import { useEvents } from '../swr/useEvents';
import { useNotes } from '../swr/useNotes';
import { useUnavailabilities } from '../swr/useUnavailabilities';
import NoteDialog from '../dialogs/NoteDialog/NoteDialog';
import { useCalendarFilter } from '../CalendarFilter/CalendarFilterContext';
import { useAreaOccupancy } from './swr/useAreaOccupancy';
import { AreasPinSource, HIGHLIGHT_REQUEST_VALIDITY_MS, useAreasView } from './AreasViewContext';
import {
  accountSchedulerConfigs,
  AgendaStatusFilter,
  cardState,
  groupAgendaBySlot,
  isNewToday,
  lateMinutes,
  matchesStatusFilter,
  nextUpOrderId,
  orderMatchesQuery,
  slotIndexOf,
  slotStartSeconds,
  statusFilterCounts,
} from './utils/occupancyMath';
import { isManuallySeated, orderHolds, pageUsesStaticAllocation } from '../utils/orderSeating';
import { configDayBounds } from './utils/workingHours';
import { blocksForArea, isClosePlaceBlock } from './utils/areaBlocks';
import { OrderPlacementType, orderPlacement } from './utils/orderAreas';
import { dayReferenceSeconds } from './utils/openBills';
import { closeDaySummary } from './utils/closeDay';
import { parseAreaDropId } from './utils/areaDrop';
import { AgendaSession, sessionMatchesQuery, sessionTitle } from './utils/agendaSessions';
import { DayNotesAlert } from './components/DayNotesAlert';
import { AreaSearchField } from './components/AreaSearchField';
import { BlockAreaDialog, BlockAreaTarget } from './components/BlockAreaDialog';
import { ClosedPlaceBanner } from './components/ClosedPlaceBanner';
import { StatusFilterButtons } from './components/StatusFilterButtons';
import { AvailabilityStrip } from './components/AvailabilityStrip';
import { AgendaList } from './components/AgendaList';
import { CloseBillWithNoteDialog } from './components/CloseBillWithNoteDialog';
import { CloseDaySection } from './components/CloseDaySection';
import { AgendaDragPreview } from './components/AgendaDragPreview';
import { PreviousDaysOpenButton } from './components/PreviousDaysOpenButton';
import { OPEN_BALANCES_SUMMARY_KEY } from '../../openBills/useOpenBalancesSummary';
import { HIDDEN_SCROLLBAR_SX } from './agendaStyles';
import { mb } from '../../common/theme/mbTokens';

const DEFAULT_SLOT_MINUTES = 30;
const itemStarts = (orders: OrderData[], events: EnrichedEventData[]) => [
  ...orders.map((order) => order.time ?? 0),
  ...events.map((event) => event.time ?? 0),
];
/** How long after the create-order drawer closes a new order still counts as "just created here". */
const CREATED_HERE_GRACE_MS = 10000;
/**
 * The agenda day view: one occupancy call feeds the availability strip, while orders, events, notes and
 * blocks are joined from the calendar's existing caches (same SWR keys, so no extra requests).
 */
type AgendaDayViewProps = {
  openEvent: (event: EnrichedEventData) => void;
};

const AgendaDayView = ({ openEvent }: AgendaDayViewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const { currentDate, startDate, endDate, editOrderDrawerState, openEditOrderDrawer, openSlotActionsMenu } =
    useCalendar();
  const { selectedOrderPageIds } = useCalendarFilter();
  const {
    selectedSeconds,
    isLive,
    nowSeconds,
    accountToday,
    pinTime,
    enterLiveMode,
    requestScrollTo,
    isStripExpanded,
    setIsStripExpanded,
    statusFilter,
    setStatusFilter,
    areaFilter,
    setAreaFilter,
    searchQuery,
    setSearchQuery,
    highlightRequest,
    requestHighlight,
  } = useAreasView();

  const configs = accountSchedulerConfigs(account);
  // Every account gets the agenda; only accounts with scheduler configs have areas, so only they fetch
  // occupancy and show the availability strip.
  const hasConfigs = configs.length > 0;
  const { occupancy, isLoading, isError, reFetch } = useAreaOccupancy({ date: currentDate, enabled: hasConfigs });
  const { items: ordersByDate, updateItem: updateOrder, reFetch: reFetchOrders } = useOrders({ startDate, endDate });
  const { items: eventsByDate } = useEvents({ startDate, endDate });
  const { items: notesByDate } = useNotes({ startDate, endDate });
  const { items: unavailabilitiesByDate, reFetch: reFetchUnavailabilities } = useUnavailabilities({
    startDate,
    endDate,
  });

  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }));
  const [draggingOrder, setDraggingOrder] = useState<OrderData | null>(null);
  const [blockTarget, setBlockTarget] = useState<BlockAreaTarget | null>(null);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState<NoteData | undefined>(undefined);
  const [orderToClose, setOrderToClose] = useState<OrderData | null>(null);

  const timeFormat = getTimeFormat(account);
  const slotMinutes = configs[0]?.slot_minutes ?? DEFAULT_SLOT_MINUTES;

  const dayOrders: OrderData[] = useMemo(() => ordersByDate[currentDate] || [], [ordersByDate, currentDate]);
  const dayEvents: EnrichedEventData[] = useMemo(() => eventsByDate[currentDate] || [], [eventsByDate, currentDate]);
  const dayNotes: NoteData[] = useMemo(() => notesByDate[currentDate] || [], [notesByDate, currentDate]);
  const dayUnavailabilities: UnavailabilityData[] = useMemo(
    () => unavailabilitiesByDate[currentDate] || [],
    [unavailabilitiesByDate, currentDate],
  );

  /** Occupancy orders carry no page id — the full order comes from the calendar's own cache. */
  const ordersById = useMemo(
    () => dayOrders.reduce<Record<string, OrderData>>((byId, order) => ({ ...byId, [order.id]: order }), {}),
    [dayOrders],
  );

  const schedulerPageIds = useMemo(
    () => new Set(configs.flatMap((config) => (config.allowed_masks ?? []).map((mask) => mask.order_page_id))),
    [configs],
  );

  /**
   * Occupancy is a separate cache from orders, and spec §9 forbids recomputing free/blocked on the
   * client — so an edited order cannot simply be patched into the pillars locally. Instead, watch
   * the orders cache (which every edit dialog updates immediately) and refetch occupancy the
   * moment anything that affects it changes. Without this, moving an order between areas only
   * showed up on the next 30-60s poll.
   */
  const occupancySignature = useMemo(
    () =>
      dayOrders
        .filter((order) => schedulerPageIds.has(order.order_page_id) || isManuallySeated(order))
        .map((order) =>
          [
            order.id,
            order.time,
            order.duration,
            order.status,
            JSON.stringify(orderHolds(order)),
            (order.resource_indexes ?? []).join('.'),
          ].join(':'),
        )
        .sort()
        .join('|'),
    [dayOrders, schedulerPageIds],
  );

  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (lastSignature.current !== null && lastSignature.current !== occupancySignature) {
      void reFetch();
    }
    lastSignature.current = occupancySignature;
  }, [occupancySignature, reFetch]);

  const isToday = currentDate === accountToday;
  const isSearching = searchQuery.trim().length > 0;

  const referenceSeconds = useMemo(
    () => dayReferenceSeconds(currentDate, accountToday, nowSeconds),
    [currentDate, accountToday, nowSeconds],
  );

  const closeDay = useMemo(() => closeDaySummary(dayOrders, referenceSeconds), [dayOrders, referenceSeconds]);

  const typeFiltered = useMemo(
    () =>
      selectedOrderPageIds
        ? dayOrders.filter((order) => selectedOrderPageIds.includes(order.order_page_id))
        : dayOrders,
    [dayOrders, selectedOrderPageIds],
  );

  const sessions: AgendaSession[] = useMemo(
    () =>
      (selectedOrderPageIds
        ? dayEvents.filter((event) => selectedOrderPageIds.includes(event.order_page_id))
        : dayEvents
      ).map((event) => ({ event, used: eventUsedTickets(event, getOrderPage(account, event.order_page_id)) })),
    [dayEvents, selectedOrderPageIds, account],
  );

  const sessionTitles: Record<string, string> = useMemo(
    () => Object.fromEntries(sessions.map(({ event }) => [event.id, sessionTitle(event, account, language)])),
    [sessions, account, language],
  );

  const placements = useMemo(
    () =>
      Object.fromEntries(typeFiltered.map((order) => [order.id, orderPlacement(order, account, occupancy, language)])),
    [typeFiltered, account, occupancy, language],
  );

  /** The strip's area filter narrows everything below it, chip counts included, like the type filter. */
  const scoped = useMemo(
    () =>
      areaFilter
        ? typeFiltered.filter((order) => {
            const placement = placements[order.id];
            return placement.type === OrderPlacementType.AREAS && placement.areas.some((a) => a.areaId === areaFilter);
          })
        : typeFiltered,
    [typeFiltered, placements, areaFilter],
  );

  const states = useMemo(
    () => Object.fromEntries(scoped.map((order) => [order.id, cardState(order, account, referenceSeconds)])),
    [scoped, account, referenceSeconds],
  );

  const unassignedIds = useMemo(
    () =>
      new Set(
        scoped.filter((order) => placements[order.id].type === OrderPlacementType.UNASSIGNED).map((order) => order.id),
      ),
    [scoped, placements],
  );

  const counts = useMemo(() => statusFilterCounts(scoped, states, unassignedIds), [scoped, states, unassignedIds]);

  const visible = useMemo(
    () =>
      scoped.filter(
        (order) =>
          matchesStatusFilter(states[order.id], unassignedIds.has(order.id), statusFilter) &&
          orderMatchesQuery(order, searchQuery),
      ),
    [scoped, states, unassignedIds, statusFilter, searchQuery],
  );

  // Sessions have no area and no status, so only the "all" chip without an area filter shows them.
  const visibleSessions = useMemo(
    () =>
      statusFilter !== AgendaStatusFilter.ALL || areaFilter
        ? []
        : sessions.filter(({ event }) => sessionMatchesQuery(sessionTitles[event.id], searchQuery)),
    [sessions, sessionTitles, statusFilter, areaFilter, searchQuery],
  );

  const groups = useMemo(
    () => groupAgendaBySlot(visible, visibleSessions, slotMinutes, account, referenceSeconds),
    [visible, visibleSessions, slotMinutes, account, referenceSeconds],
  );

  const nextUpId = useMemo(
    () => nextUpOrderId(scoped, states, nowSeconds, isToday),
    [scoped, states, nowSeconds, isToday],
  );

  const newIds = useMemo(
    () => new Set(scoped.filter((order) => isNewToday(order, accountToday, account.timezone)).map((order) => order.id)),
    [scoped, accountToday, account.timezone],
  );

  const lateById = useMemo(
    () =>
      Object.fromEntries(scoped.map((order) => [order.id, lateMinutes(order, states[order.id], nowSeconds, isToday)])),
    [scoped, states, nowSeconds, isToday],
  );

  // The day change resets the filters and moves the list; it must not re-run when the orders or the
  // clock tick, so those are read through refs.
  const dayOrdersRef = useRef(dayOrders);
  dayOrdersRef.current = dayOrders;
  const dayEventsRef = useRef(dayEvents);
  dayEventsRef.current = dayEvents;
  /** A date whose first pin was made before its orders or sessions arrived; re-pinned once, when they do. */
  const awaitingOrdersForDateRef = useRef<string | null>(null);
  const nowSecondsRef = useRef(nowSeconds);
  nowSecondsRef.current = nowSeconds;
  const isTodayRef = useRef(isToday);
  isTodayRef.current = isToday;
  const configsRef = useRef(configs);
  configsRef.current = configs;

  /**
   * Where a day other than today opens: the slot of its first order or session, else the earliest configured
   * opening, snapped to the slot grid so the pin and the list's scroll target name the same group.
   */
  const pinToFirstSlot = (itemStarts: number[]) => {
    const configStarts = configsRef.current
      .map((config) => configDayBounds(config, account, currentDate)?.startSeconds)
      .filter((start): start is number => start !== undefined);
    const firstStart =
      itemStarts.length > 0 ? Math.min(...itemStarts) : configStarts.length > 0 ? Math.min(...configStarts) : 0;
    const firstSlotStart = slotStartSeconds(slotIndexOf(firstStart, slotMinutes), slotMinutes);
    pinTime(firstSlotStart, AreasPinSource.EXPLICIT);
    requestScrollTo(firstSlotStart);
  };
  const pinToFirstSlotRef = useRef(pinToFirstSlot);
  pinToFirstSlotRef.current = pinToFirstSlot;

  const consumedHighlightTokenRef = useRef<number | null>(null);
  const pendingHighlightFor = (date: string) =>
    highlightRequest &&
    highlightRequest.date === date &&
    highlightRequest.token !== consumedHighlightTokenRef.current &&
    performance.now() - highlightRequest.at <= HIGHLIGHT_REQUEST_VALIDITY_MS
      ? highlightRequest
      : null;
  const pendingHighlightRef = useRef(pendingHighlightFor);
  pendingHighlightRef.current = pendingHighlightFor;

  const focusHighlightedSlot = (pending: { seconds: number; token: number }) => {
    consumedHighlightTokenRef.current = pending.token;
    setIsStripExpanded(true);
    setStatusFilter(AgendaStatusFilter.ALL);
    setAreaFilter(null);
    setSearchQuery('');
    pinTime(slotStartSeconds(slotIndexOf(pending.seconds, slotMinutes), slotMinutes), AreasPinSource.EXPLICIT);
  };
  const focusHighlightedSlotRef = useRef(focusHighlightedSlot);
  focusHighlightedSlotRef.current = focusHighlightedSlot;

  useEffect(() => {
    setStatusFilter(AgendaStatusFilter.ALL);
    setAreaFilter(null);
    setSearchQuery('');
    awaitingOrdersForDateRef.current = null;
    const pending = pendingHighlightRef.current(currentDate);
    if (pending) {
      focusHighlightedSlotRef.current(pending);
      return;
    }
    if (isTodayRef.current) {
      enterLiveMode();
      requestScrollTo(nowSecondsRef.current, true);
      return;
    }
    pinToFirstSlotRef.current(itemStarts(dayOrdersRef.current, dayEventsRef.current));
    if (dayOrdersRef.current.length + dayEventsRef.current.length === 0) {
      awaitingOrdersForDateRef.current = currentDate;
    }
    // Only a day change re-runs this; everything else is read through refs or is a stable callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  // Same-day creation: the day does not change, so the effect above stays quiet and this one frames the order.
  useEffect(() => {
    const pending = pendingHighlightRef.current(currentDate);
    if (pending) {
      focusHighlightedSlotRef.current(pending);
    }
    // Only a new request re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightRequest?.token]);

  const consumedEntityIdRef = useRef(false);
  useEffect(() => {
    if (consumedEntityIdRef.current) return;
    const urlParams = new URLSearchParams(window.location.search);
    const entityId = urlParams.get('entityid');
    if (!entityId) return;
    const item = ordersById[entityId] || dayEvents.find((event) => event.id === entityId);
    if (!item) return;
    consumedEntityIdRef.current = true;
    requestHighlight(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersById, dayEvents]);

  // The one-shot follow-up for a cold cache: the day's orders or sessions arrive after the first pin, so pin
  // once more to their first slot. Deliberately not a dependency of the effect above - a later add, delete
  // or poll must never reset the filters or move a time the user picked.
  useEffect(() => {
    if (awaitingOrdersForDateRef.current === currentDate && dayOrders.length + dayEvents.length > 0) {
      awaitingOrdersForDateRef.current = null;
      pinToFirstSlotRef.current(itemStarts(dayOrders, dayEvents));
    }
  }, [dayOrders, dayEvents, currentDate]);

  /**
   * The strip starts folded. After an order is created from this page it opens, so the admin sees
   * where the new booking landed and what is left. Detected as a new order id arriving while the
   * create drawer is open or has just closed; a reload or a day change does not count.
   */
  const isCreateDrawerOpen = Boolean(editOrderDrawerState?.isOpen && !editOrderDrawerState.order);
  const createDrawerClosedAtRef = useRef<number | null>(null);
  const wasCreateDrawerOpenRef = useRef(false);
  useEffect(() => {
    if (wasCreateDrawerOpenRef.current && !isCreateDrawerOpen) {
      createDrawerClosedAtRef.current = performance.now();
    }
    wasCreateDrawerOpenRef.current = isCreateDrawerOpen;
  }, [isCreateDrawerOpen]);
  const knownOrderIdsRef = useRef<{ date: string; ids: Set<string> } | null>(null);
  useEffect(() => {
    const ids = new Set(dayOrders.map((order) => order.id));
    const known = knownOrderIdsRef.current;
    knownOrderIdsRef.current = { date: currentDate, ids };
    if (!known || known.date !== currentDate) {
      return;
    }
    const hasNewOrder = [...ids].some((id) => !known.ids.has(id));
    const justClosed =
      createDrawerClosedAtRef.current !== null &&
      performance.now() - createDrawerClosedAtRef.current < CREATED_HERE_GRACE_MS;
    if (hasNewOrder && (isCreateDrawerOpen || justClosed)) {
      setIsStripExpanded(true);
    }
  }, [dayOrders, currentDate, isCreateDrawerOpen, setIsStripExpanded]);

  const onDragStart = ({ active }: DragStartEvent) => {
    setDraggingOrder(ordersById[active.id] ?? null);
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setDraggingOrder(null);
    const target = over ? parseAreaDropId(over.id) : null;
    const order = ordersById[active.id];
    if (!target || !order) {
      return;
    }
    const placement = placements[order.id];
    const isAlreadyThere =
      placement?.type === OrderPlacementType.AREAS &&
      placement.areas.length === 1 &&
      placement.areas[0].configId === target.schedulerConfigId &&
      placement.areas[0].areaId === target.areaId;
    if (isAlreadyThere) {
      return;
    }
    const targetArea = occupancy?.scheduler_configs
      .find((config) => config.scheduler_config_id === target.schedulerConfigId)
      ?.areas.find((area) => area.area_id === target.areaId);
    const areaLabel = targetArea ? localizedLabel(targetArea.label, language, targetArea.area_id) : target.areaId;
    const wholeArea = pageUsesStaticAllocation(order, account);
    try {
      const updated = await executeWithConflictCheck(
        () =>
          OrderService.assignOrderToArea({
            order_id: order.id,
            scheduler_config_id: target.schedulerConfigId,
            area_id: target.areaId,
            whole_area: wholeArea,
          }),
        t('calendar.areas_view.moved_to_area', { area: areaLabel }),
      );
      await updateOrder(updated);
    } catch {
      // the handler showed the conflict or error
    }
  };

  const formatAmount = (amount: number) =>
    priceFormatterFromCurrency(amount, account.currency, undefined, false, false);

  const afterMutation = async () => {
    await Promise.all([reFetch(), reFetchUnavailabilities()]);
  };

  if (hasConfigs && isLoading && !occupancy) {
    return (
      <div className="loading-container">
        <RotatingLoader />
      </div>
    );
  }

  if (hasConfigs && (isError || !occupancy)) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {t('calendar.areas_view.load_error')}
      </Alert>
    );
  }

  const availabilityStrip = occupancy && (
    <AvailabilityStrip
      occupancy={occupancy}
      configs={configs}
      account={account}
      dayOrders={dayOrders}
      dayUnavailabilities={dayUnavailabilities}
      selectedSeconds={selectedSeconds}
      nowSeconds={isToday ? nowSeconds : null}
      isLive={isLive}
      currentDate={currentDate}
      timeFormat={timeFormat}
      language={language}
      isExpanded={isStripExpanded}
      onToggleExpanded={() => setIsStripExpanded(!isStripExpanded)}
      areaFilter={areaFilter}
      onToggleAreaFilter={(areaId) => setAreaFilter(areaFilter === areaId ? null : areaId)}
      onPinTime={(seconds) => {
        pinTime(seconds);
        requestScrollTo(seconds);
      }}
      onOpenSlotActions={
        isAtLeastSupervisor
          ? (seconds, cell, target, areaLabel) => {
              const rect = cell.getBoundingClientRect();
              openSlotActionsMenu({
                slot: { date: currentDate, startSeconds: seconds },
                anchorPosition: { top: rect.bottom, left: rect.left + rect.width / 2 },
                // The click above pinned this time, which the block dialog starts from.
                areaBlock: { areaLabel, open: () => setBlockTarget(target) },
              });
            }
          : undefined
      }
      canManageBlocks={isAtLeastSupervisor}
      onManageBlocks={setBlockTarget}
      isDropEnabled={!isSmallScreen}
    />
  );

  const sidePanel = (
    <Grid
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        gap: 1.75,
        // Desktop: a 4px inset (cancelled by the negative margin) keeps focus rings from being clipped by the scroll.
        px: isSmallScreen ? 1.5 : 0.5,
        pb: isSmallScreen ? 1 : 0.5,
        ...(!isSmallScreen && { mx: -0.5, mb: -0.5 }),
        ...(isSmallScreen && {
          maxHeight: '60%',
          overflowY: 'auto',
          ...HIDDEN_SCROLLBAR_SX,
        }),
        ...(!isSmallScreen && {
          flex: '1 1 0',
          minWidth: 0,
          overflowY: 'auto',
          ...HIDDEN_SCROLLBAR_SX,
        }),
      }}>
      <AreaSearchField
        value={searchQuery}
        resultCount={isSearching ? visible.length + visibleSessions.length : null}
        onChange={setSearchQuery}
      />
      <StatusFilterButtons
        counts={counts}
        value={statusFilter}
        onChange={setStatusFilter}
        leading={isSmallScreen ? <PreviousDaysOpenButton /> : undefined}
      />
      {availabilityStrip}

      <ClosedPlaceBanner
        blocks={dayUnavailabilities.filter((unavailability) => isClosePlaceBlock(unavailability, configs))}
        timeFormat={timeFormat}
        canReopen={isAtLeastSupervisor}
        onReopened={afterMutation}
      />

      <DayNotesAlert
        notes={dayNotes}
        timeFormat={timeFormat}
        onAddNote={() => {
          setNoteToEdit(undefined);
          setIsNoteDialogOpen(true);
        }}
        onOpenNote={(note) => {
          setNoteToEdit(note);
          setIsNoteDialogOpen(true);
        }}
      />
    </Grid>
  );

  const list = (
    <AgendaList
      groups={groups}
      states={states}
      placements={placements}
      sessionTitles={sessionTitles}
      onOpenSession={openEvent}
      nextUpId={nextUpId}
      newIds={newIds}
      lateById={lateById}
      account={account}
      language={language}
      timeFormat={timeFormat}
      isToday={isToday}
      nowSeconds={nowSeconds}
      isDragEnabled={!isSmallScreen && hasConfigs}
      emptyMessage={
        dayOrders.length === 0 && dayEvents.length === 0
          ? t('calendar.areas_view.no_orders_today')
          : t('calendar.areas_view.no_orders_match_filter')
      }
      footer={
        currentDate <= accountToday && closeDay.totalCount > 0 ? (
          <CloseDaySection
            summary={closeDay}
            timeFormat={timeFormat}
            formatAmount={formatAmount}
            onCollect={(order) => openEditOrderDrawer({ order, focusExtraItems: true, openCheckout: true })}
            onCloseWithNote={setOrderToClose}
          />
        ) : undefined
      }
    />
  );

  return (
    <Grid sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/*
        Phone: the panel stacks above the list and stays put while the list scrolls underneath.
        Desktop: the list is the main column (the reading start side) and the panel sits beside it,
        so the list gets the full height instead of what the stacked panel leaves over.
      */}
      <Grid
        sx={{
          width: '100%',
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          gap: isSmallScreen ? 0 : 3,
          px: isSmallScreen ? 0 : mb.layout.pageInset.md,
          pt: isSmallScreen ? 0 : 1.5,
          pb: isSmallScreen ? 0 : 2,
        }}>
        {isSmallScreen ? (
          <>
            {sidePanel}
            {list}
          </>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDraggingOrder(null)}>
            <Grid sx={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {list}
            </Grid>
            {sidePanel}
            <DragOverlay>
              {draggingOrder && <AgendaDragPreview order={draggingOrder} timeFormat={timeFormat} />}
            </DragOverlay>
          </DndContext>
        )}
      </Grid>

      {blockTarget && (
        <BlockAreaDialog
          target={blockTarget}
          account={account}
          currentDate={currentDate}
          selectedSeconds={selectedSeconds}
          dayUnavailabilities={dayUnavailabilities}
          orders={blockTarget.occupancyConfig.order_ids.map((orderId) => ordersById[orderId]).filter(Boolean)}
          areaBlocks={blocksForArea(dayUnavailabilities, blockTarget.occupancyConfig, blockTarget.area)}
          timeFormat={timeFormat}
          language={language}
          onClose={() => setBlockTarget(null)}
          onBlocked={afterMutation}
        />
      )}

      {orderToClose && (
        <CloseBillWithNoteDialog
          order={orderToClose}
          timeFormat={timeFormat}
          formatAmount={formatAmount}
          onClose={() => setOrderToClose(null)}
          onClosed={async (updatedOrder) => {
            await updateOrder(updatedOrder);
            void mutate(OPEN_BALANCES_SUMMARY_KEY);
            setOrderToClose(null);
          }}
          onStale={() => {
            setOrderToClose(null);
            void reFetchOrders();
            void mutate(OPEN_BALANCES_SUMMARY_KEY);
          }}
        />
      )}

      {isNoteDialogOpen && (
        <NoteDialog
          open={isNoteDialogOpen}
          setOpen={setIsNoteDialogOpen}
          note={noteToEdit}
          calendarStartDate={startDate}
          calendarEndDate={endDate}
        />
      )}
    </Grid>
  );
};

export default AgendaDayView;
